import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import jsPDF from "jspdf";
import { hasMultipleResponsibleTargets } from "../utils/responsibles";
import DynamicSelectionField from "../components/DynamicSelectionField";
import { applyAttachmentTemplate } from "../utils/attachmentNaming";
import StepHistory from "../components/StepHistory";
import TableField from "../components/TableField";
import { FaHistory } from "react-icons/fa";
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from "../utils/uploadLimits";
import { emitDashboardRefresh } from "../utils/dashboardEvents";



const getAutoFillValue = (source, user) => {
  if (!source) return "";
  const today = new Date().toISOString().slice(0, 10);
  const sourceMap = {
    user_name: user?.name || "",
    today_date: today,
    user_email: user?.email || "",
    user_cpf: user?.cpf || "",
    user_birth_date: user?.birthDate ? String(user.birthDate).slice(0, 10) : "",
    user_sector: user?.sector?.name || "",
  };
  return sourceMap[source] ?? "";
};

const applyAutoFillDefaults = (fields = [], values = {}, user) => {
  const nextValues = { ...values };
  fields.forEach((field) => {
    if (!field?.id || !field.autoFillSource) return;
    const currentValue = nextValues[field.id];
    const isEmpty =
      currentValue === undefined ||
      currentValue === null ||
      currentValue === "";
    if (!isEmpty) return;
    const prefilled = getAutoFillValue(field.autoFillSource, user);
    if (prefilled !== "") {
      nextValues[field.id] = prefilled;
    }
  });
  return nextValues;
};

function fieldVisible(field, values) {
  if (!field.visibleWhen) return true;
  try {
    const fn = new Function(
      "values",
      `with (values) { return (${field.visibleWhen}); }`,
    );
    return !!fn(values);
  } catch (error) {
    console.error("Erro ao avaliar condição de visibilidade", error);
    return false;
  }
}

export default function ProcessRunner({
  processId,
  instanceId,
  onStart,
  onFinish,
  closeTab,
  activateTab,
  returnToTabId = 'requests',
}) {
  const { user } = useAuth();
  const [instance, setInstance] = useState(null);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [pendingEvents, setPendingEvents] = useState([]);
  const [triggeringEventId, setTriggeringEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkedFieldOptions, setLinkedFieldOptions] = useState({});
  const [claimingTask, setClaimingTask] = useState(false);
  const [reassigningTask, setReassigningTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const currentResponsibleEntry = useMemo(() => {
    if (!instance) return null;
    const stepKey = String(instance.currentStep ?? 0);
    const fromInstance = instance.stepResponsibles?.[stepKey];
    const fromProcess = instance.process?.stepResponsibles?.[stepKey];
    return fromInstance || fromProcess || null;
  }, [instance]);

  const mustClaimBeforeSubmit = useMemo(() => {
    if (!instance) return false;
    if (instance.currentAssignee && !instance.isCurrentAssignee) return true;
    if (instance.canClaim && !instance.isCurrentAssignee) return true;
    if (!instance.currentAssignee && !instance.canClaim) {
      return hasMultipleResponsibleTargets(currentResponsibleEntry);
    }
    return false;
  }, [instance, currentResponsibleEntry]);

  const currentStepName = useMemo(() => {
    if (!instance) return null;
    const names = instance.process?.stepNames;
    const currentIndex = instance.currentStep ?? 0;
    if (!names) return null;
    if (Array.isArray(names)) {
      const value = names[currentIndex];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    if (typeof names === "object" && names !== null) {
      const key = String(currentIndex);
      const value = names[key];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    return null;
  }, [instance]);

  const baseInputClasses = useMemo(
    () =>
      "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-200",
    [],
  );
  const readOnlyInputClasses =
    "bg-slate-100 text-slate-500 border-slate-200 shadow-none focus:border-slate-300 focus:ring-0";
  const getInputClassName = (isReadOnlyOrDisabled = false) =>
    `${baseInputClasses}${isReadOnlyOrDisabled ? ` ${readOnlyInputClasses}` : ""}`;

  const renderFieldControl = (
    field,
    {
      readOnly = false,
      allowSelectionWhenReadOnly = false,
    } = {},
  ) => {
    const value = values[field.id];
    const normalizedValue = value ?? "";
    const fieldLockedByAutoFill = Boolean(field.autoFillSource) && field.autoFillEditable === false;
    const isReadOnlyField = readOnly || fieldLockedByAutoFill;

    if (field.type === "list") {
      const listValue = field.allowMultiple
        ? Array.isArray(value)
          ? value
          : value
            ? [value]
            : []
        : normalizedValue;

      return (
        <select
          id={field.id}
          name={field.id}
          value={listValue}
          onChange={handleChange}
          disabled={isReadOnlyField}
          required={field.required}
          className={`${getInputClassName(isReadOnlyField)} appearance-none`}
          multiple={field.allowMultiple}
        >
          {!field.allowMultiple && (
            <option value="" disabled>
              Selecione...
            </option>
          )}
          {(field.options || []).map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (field.type === "linkedField") {
      const options = Array.isArray(linkedFieldOptions[field.id]) ? linkedFieldOptions[field.id] : [];
      const listValue = field.multiple
        ? Array.isArray(value) ? value : value ? [value] : []
        : (value ?? "");
      return (
        <select
          id={field.id}
          name={field.id}
          value={listValue}
          onChange={handleChange}
          disabled={isReadOnlyField}
          required={field.required}
          className={`${getInputClassName(isReadOnlyField)} appearance-none`}
          multiple={Boolean(field.multiple)}
        >
          {!field.multiple && <option value="">Selecione...</option>}
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.displayValue} {option.requestId ? `(#${option.requestId})` : ""}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "table") {
      return (
        <TableField
          field={field}
          value={value}
          onChange={(nextRows) => {
            setValues((prev) => {
              const updated = { ...prev, [field.id]: nextRows };
              if (instance) saveDraft(instance.id, instance.currentStep, updated);
              return updated;
            });
          }}
          readOnly={isReadOnlyField}
          inputClassName={getInputClassName(isReadOnlyField)}
          onUploadFile={isReadOnlyField ? undefined : ({ rowIndex, column, file }) => handleTableFileChange(field, rowIndex, column, file)}
        />
      );
    }

    if (field.type === "dynamic-select") {
      return (
        <>
          <DynamicSelectionField
            value={value}
            onChange={(nextValue) =>
              handleDynamicSelectionChange(field.id, nextValue)
            }
            readOnly={isReadOnlyField}
            allowSelectionWhenReadOnly={allowSelectionWhenReadOnly}
            allowMultipleDefault={Boolean(field.allowMultiple)}
            required={field.required}
          />
          {field.lockedForStep && (
            <p className="text-xs text-indigo-600">
              Este campo está bloqueado para edição nesta etapa.
            </p>
          )}
        </>
      );
    }

    if (field.type === "file") {
      if (readOnly) {
        return <FileAttachment file={value} />;
      }
      return (
        <div className="space-y-2">
          {value && <FileAttachment file={value} />}
          <input
            type="file"
            id={field.id}
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              if (!selectedFile) return;
              if (selectedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
                alert(getMaxUploadFileSizeMessage());
                event.target.value = "";
                return;
              }
              handleFileChange(field.id, selectedFile);
            }}
            required={field.required}
            className="w-full cursor-pointer rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 file:mr-4 file:cursor-pointer file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-600 hover:file:bg-indigo-100"
          />
          <p className="text-xs text-slate-500">
            Tamanho máximo do arquivo: {MAX_UPLOAD_FILE_SIZE_MB} MB.
          </p>
        </div>
      );
    }

    if (field.type === "decimal") {
      return (
        <DecimalInput
          decimalPlaces={field.decimalPlaces || 0}
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "number") {
      return (
        <IntegerInput
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "phone") {
      return (
        <MaskedInput
          mask="phone"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cpf") {
      return (
        <MaskedInput
          mask="cpf"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cnpj") {
      return (
        <MaskedInput
          mask="cnpj"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cep") {
      return (
        <MaskedInput
          mask="cep"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "email") {
      return (
        <input
          type="email"
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          inputMode="email"
          autoComplete="email"
          pattern="[^@\\s]+@[^@\\s]+\\.[^@\\s]+"
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "date") {
      return (
        <input
          type="date"
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          onFocus={(event) => event.target.showPicker?.()}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "time") {
      return (
        <input
          type="time"
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "textarea") {
      const textareaRows = Math.max(3, Number(field.rows) || 0);
      return (
        <textarea
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          rows={textareaRows}
          className={`${getInputClassName(isReadOnlyField)} resize-y`}
        />
      );
    }

    const inputType = field.type || "text";
    return (
      <input
        type={inputType}
        id={field.id}
        name={field.id}
        value={normalizedValue}
        onChange={handleChange}
        readOnly={readOnly}
        required={field.required}
        className={getInputClassName(readOnly)}
      />
    );
  };

  const draftKey = (instId, step) => {
    const userPart = user?.id ? `user-${user.id}` : "anon";
    const stepPart = Number.isInteger(step) ? `step-${step}` : "step-unknown";
    return `draft-${userPart}-${instId}-${stepPart}`;
  };

  const loadDraft = (instId, step) => {
    try {
      const d = localStorage.getItem(draftKey(instId, step));
      return d ? JSON.parse(d) : {};
    } catch {
      return {};
    }
  };

  const saveDraft = (instId, step, vals) => {
    try {
      localStorage.setItem(draftKey(instId, step), JSON.stringify(vals));
    } catch {}
  };

  const clearDraft = (instId) => {
    try {
      const userPart = user?.id ? `user-${user.id}` : "anon";
      const prefix = `draft-${userPart}-${instId}-`;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {}
  };

  const handleClaimTask = async () => {
    if (!instance) return;
    setClaimingTask(true);
    try {
      const res = await api.post(`/processes/instances/${instance.id}/claim`);
      setInstance(res.data);
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao assumir tarefa.";
      alert(message);
    } finally {
      setClaimingTask(false);
    }
  };

  const handleReleaseTask = async () => {
    if (!instance) return;
    setReassigningTask(true);
    try {
      const res = await api.post(`/processes/instances/${instance.id}/reassign`, {
        userId: null,
      });
      setInstance(res.data);
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao liberar tarefa.";
      alert(message);
    } finally {
      setReassigningTask(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        let res;
        if (instanceId) {
          res = await api.get(`/processes/instances/${instanceId}/form`);
        } else if (processId) {
          res = await api.post(`/processes/${processId}/start-form`);
        }
        if (!res) {
          setLoading(false);
          return;
        }
        const inst = res.data.instance;
        let form = res.data.form;
        const events = Array.isArray(res.data.waitingEvents)
          ? res.data.waitingEvents
          : [];
        setPendingEvents(events);
        if (!form && inst.process?.formId) {
          try {
            const fRes = await api.get(`/forms/${inst.process.formId}`);
            form = fRes.data;
          } catch {}
        }
        setInstance(inst);
        setForm(form);
        const draft = form ? loadDraft(inst.id, inst.currentStep) : {};
        const saved = inst.formData || {};
        const isExistingInstance = Boolean(instanceId);
        if (form) {
          const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
          const baseValues = isExistingInstance
            ? (Object.keys(draft).length ? draft : saved)
            : {};
          setValues(applyAutoFillDefaults(schemaFields, baseValues, user));
        } else {
          setValues(isExistingInstance ? saved : {});
        }
        if (onStart) onStart(inst);
      } catch (err) {
        const message =
          err.response?.data?.error || "Erro ao carregar formulário";
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [processId, instanceId, user]);

  useEffect(() => {
    setShowHistory(false);
  }, [instance?.id]);

  useEffect(() => {
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    const linkedFields = schemaFields.filter((field) => field?.type === "linkedField" && field?.linkedFieldId);
    if (!linkedFields.length) {
      setLinkedFieldOptions({});
      return;
    }
    let active = true;
    Promise.all(
      linkedFields.map(async (field) => {
        const { data } = await api.get("/forms/linked-field/options", {
          params: {
            linkedProcessId: field.linkedProcessId || undefined,
            linkedFormId: field.linkedFormId || undefined,
            linkedFieldId: field.linkedFieldId,
            sourceStatus: field.sourceStatus || "completed",
          },
        });
        return [field.id, data?.options || []];
      })
    )
      .then((entries) => {
        if (!active) return;
        setLinkedFieldOptions(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!active) return;
        setLinkedFieldOptions({});
      });
    return () => {
      active = false;
    };
  }, [form]);

  useEffect(() => {
    return () => {
      if (instance) {
        saveDraft(instance.id, instance.currentStep, values);
      }
    };
  }, [instance, values]);

  useEffect(() => {
    if (!loading && instance && typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, [loading, instance?.id]);

  const handleChange = (e) => {
    const { name, value, options, multiple } = e.target;
    const nextValue = multiple
      ? Array.from(options || [])
          .filter((option) => option.selected)
          .map((option) => option.value)
      : value;

    setValues((v) => {
      const updated = { ...v, [name]: nextValue };
      if (instance) saveDraft(instance.id, instance.currentStep, updated);
      return updated;
    });
  };

  const handleFileChange = async (name, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }
    const field = form?.schema?.fields?.find((f) => f.id === name);
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    let selectedFile = file;
    let generatedFileName = null;
    if (field?.fileNameTemplate) {
      const { file: renamedFile, generatedName } = applyAttachmentTemplate(file, field.fileNameTemplate, {
        values,
        fields: schemaFields,
        originalFileName: file.name,
        extras: {
          requestId: instance?.id,
          requestProtocol: instance?.protocol,
          solicitante:
            instance?.requester?.name ||
            instance?.user?.name ||
            instance?.startedBy?.name ||
            "",
        },
      });
      selectedFile = renamedFile;
      generatedFileName = generatedName;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    const category =
      instance?.process?.category || instance?.process?.name || "processo-sem-categoria";
    const formName = form?.name || instance?.process?.name || "Formulário";
    const fieldTitle = field?.label || name;
    formData.append("category", category);
    formData.append("formName", formName);
    formData.append("fieldTitle", fieldTitle);
    formData.append("insertedAt", new Date().toISOString());
    if (generatedFileName) {
      formData.append("generatedFileName", generatedFileName);
    }
    if (instance) {
      formData.append("requestId", String(instance.id));
      if (instance.protocol) {
        formData.append("requestProtocol", instance.protocol);
      }
      const requesterName =
        instance.requester?.name ||
        instance.user?.name ||
        instance.startedBy?.name ||
        "";
      if (requesterName) {
        formData.append("requesterName", requesterName);
      }
      const requesterSector =
        instance.requester?.sector?.name ||
        instance.user?.sector?.name ||
        instance.startedBy?.sector?.name ||
        "";
      if (requesterSector) {
        formData.append("requesterSector", requesterSector);
      }
    }
    try {
      const res = await api.post("/upload", formData);
      const fileInfo = {
        url: res.data.url,
        downloadUrl: res.data.downloadUrl,
        downloadPath: res.data.downloadPath,
        relativePath: res.data.relativePath,
        name: res.data.name,
        metadata: res.data.metadata,
      };
      setValues((v) => {
        const updated = { ...v, [name]: fileInfo };
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            "Erro ao enviar arquivo";
      alert(message);
    }
  };

  const handleTableFileChange = async (field, rowIndex, column, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const category =
      instance?.process?.category || instance?.process?.name || "processo-sem-categoria";
    const formName = form?.name || instance?.process?.name || "Formulário";
    formData.append("category", category);
    formData.append("formName", formName);
    formData.append("fieldTitle", `${field?.label || field?.id} - ${column?.label || column?.id}`);
    formData.append("insertedAt", new Date().toISOString());

    try {
      const res = await api.post("/upload", formData);
      const fileInfo = {
        url: res.data.url,
        downloadUrl: res.data.downloadUrl,
        downloadPath: res.data.downloadPath,
        relativePath: res.data.relativePath,
        name: res.data.name,
        metadata: res.data.metadata,
      };
      setValues((prev) => {
        const rows = Array.isArray(prev[field.id]) ? [...prev[field.id]] : [];
        const row = rows[rowIndex] && typeof rows[rowIndex] === "object" ? rows[rowIndex] : {};
        rows[rowIndex] = {
          ...row,
          [column.id]: fileInfo,
        };
        const updated = { ...prev, [field.id]: rows };
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            "Erro ao enviar arquivo";
      alert(message);
    }
  };

  const handleDynamicSelectionChange = (fieldId, nextValue) => {
    setValues((prev) => {
      const updated = { ...prev, [fieldId]: nextValue };
      if (instance) saveDraft(instance.id, instance.currentStep, updated);
      return updated;
    });
  };

  const handleDownloadPdf = () => {
    if (!instance) return;
    const doc = new jsPDF();
    doc.setLineWidth(0.1);
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const colWidth = contentWidth / 12;
    const headerY = margin;

    doc.setFontSize(14);
    doc.text(
      `Solicitação ${instance.id} - ${instance.process.name}`,
      margin,
      headerY,
    );
    doc.setFontSize(11);
    const requester =
      instance.requester?.name ||
      instance.user?.name ||
      instance.startedBy?.name ||
      "-";
    const headerLines = [
      `ID: ${instance.id}`,
      `Processo: ${instance.process.name}`,
      `Solicitante: ${requester}`,
      `Início: ${new Date(instance.startedAt).toLocaleString()}`,
    ];
    headerLines.forEach((l, idx) => {
      doc.text(l, margin, headerY + 8 + idx * 6);
    });

    let x = margin;
    let y = headerY + 8 + headerLines.length * 6 + 2;
    const rowHeight = 18;
    const rowSpacing = 0;
    const groupSpacing = 2;
    let remaining = 12;

    const fieldMap = (form.schema?.fields || []).reduce(
      (acc, f) => ({ ...acc, [f.id]: f }),
      {},
    );

    const groups = form.schema?.groups || [];
    const ungrouped = {
      id: "__ungrouped__",
      label: groups.length > 0 ? "Campos" : "",
    };
    const orderedGroups = [...groups, ungrouped];

    orderedGroups.forEach((g) => {
      const gFields = (form.schema?.fields || [])
        .filter(
          (f) => (f.groupId || "__ungrouped__") === (g.id || "__ungrouped__"),
        )
        .filter((f) => Object.prototype.hasOwnProperty.call(values, f.id));
      if (gFields.length === 0) return;

      if (g.label) {
        doc.setFontSize(12);
        // draw top and side lines only so the first field row connects seamlessly
        doc.line(margin, y, margin + contentWidth, y);
        doc.line(margin, y, margin, y + 8);
        doc.line(margin + contentWidth, y, margin + contentWidth, y + 8);
        doc.text(g.label, margin + contentWidth / 2, y + 5, {
          align: "center",
        });
        y += 8; // header should touch first field row
      }

      x = margin;
      remaining = 12;

      gFields.forEach((f) => {
        const size = f.size || 12;
        const width = size * colWidth;

        if (size > remaining) {
          x = margin;
          y += rowHeight + rowSpacing;
          remaining = 12;
        }

        let val = values[f.id];
        if (val && typeof val === "object") {
          if (Array.isArray(val.selected) && Array.isArray(val.options)) {
            const optionMap = new Map();
            val.options.forEach((option) => {
              if (!option) return;
              if (typeof option === "string") {
                optionMap.set(option, option);
                return;
              }
              const optId = option.id ?? option.value ?? option.label ?? option.name;
              if (optId === undefined || optId === null) return;
              const label = option.label ?? option.name ?? option.value ?? String(optId);
              optionMap.set(String(optId), String(label));
            });
            const selectedLabels = Array.isArray(val.selected)
              ? val.selected
                  .map((selectedId) => optionMap.get(String(selectedId)) || String(selectedId))
                  .filter((text) => typeof text === "string" && text.trim().length > 0)
              : [];
            if (selectedLabels.length > 0) {
              val = selectedLabels.join(", ");
            } else if (optionMap.size > 0) {
              val = `Opções: ${Array.from(optionMap.values()).join(", ")}`;
            } else {
              val = "";
            }
          } else if (val.name) val = val.name;
          else val = JSON.stringify(val);
        }

        doc.rect(x, y, width, rowHeight);
        doc.setFontSize(10);
        doc.text(f.label, x + 2, y + 5);
        doc.setFontSize(12);
        const lines = doc.splitTextToSize(String(val ?? ""), width - 4);
        doc.text(lines, x + 2, y + 12);

        x += width; // keep fields connected
        remaining -= size;
      });

      y += rowHeight + groupSpacing;
    });

    doc.save(`solicitacao-${instance.id}.pdf`);
  };

  const handleTriggerEvent = async (eventInfo) => {
    if (!instance || !eventInfo?.id) return;
    try {
      setTriggeringEventId(eventInfo.id);
      await api.post(`/processes/instances/${instance.id}/events`, {
        eventId: eventInfo.id,
      });
      const res = await api.get(`/processes/instances/${instance.id}/form`);
      const inst = res.data.instance;
      const nextForm = res.data.form || null;
      const waiting = Array.isArray(res.data.waitingEvents)
        ? res.data.waitingEvents
        : [];
      setInstance(inst);
      setForm(nextForm);
      setPendingEvents(waiting);
      clearDraft(inst.id);
      const saved = inst?.formData || {};
      const schemaFields = Array.isArray(nextForm?.schema?.fields) ? nextForm.schema.fields : [];
      setValues(applyAutoFillDefaults(schemaFields, saved, user));
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao registrar evento.";
      alert(message);
    } finally {
      setTriggeringEventId(null);
    }
  };

  const handleSubmit = async () => {
    if (!instance || submitting) return;
    const reqFields = (form.schema?.fields || []).filter(
      (f) => f.required && !f.readOnly && fieldVisible(f, values),
    );
    for (const f of reqFields) {
      const v = values[f.id];
      if (f.type === "dynamic-select") {
        const optionCount = Array.isArray(v?.options) ? v.options.length : 0;
        if (optionCount === 0) {
          alert(`Adicione opções no campo "${f.label}" antes de prosseguir.`);
          return;
        }
        const selectedCount = Array.isArray(v?.selected) ? v.selected.length : 0;
        if (selectedCount === 0) {
          alert(`Selecione ao menos uma opção no campo "${f.label}".`);
          return;
        }
        continue;
      }
      if (f.type === "table") {
        const rows = Array.isArray(v) ? v : [];
        if (rows.length === 0) {
          alert(`Adicione ao menos uma linha no campo "${f.label}".`);
          return;
        }
        const requiredColumns = Array.isArray(f.tableColumns)
          ? f.tableColumns.filter((column) => column.required)
          : [];
        for (const column of requiredColumns) {
          const hasMissing = rows.some((row) => {
            const cell = row?.[column.id];
            return cell === undefined || cell === null || cell === "";
          });
          if (hasMissing) {
            alert(`Preencha a coluna obrigatória "${column.label}" em "${f.label}".`);
            return;
          }
        }
        continue;
      }
      if (v === undefined || v === "") {
        alert(`O campo "${f.label}" é obrigatório`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/processes/instances/${instance.id}/submit`,
        values,
      );
      emitDashboardRefresh();
      const savedValues = res.data.formData || {};
      const step = instance.currentStep;
      const waiting = Array.isArray(res.data.waitingEvents)
        ? res.data.waitingEvents
        : [];
      setPendingEvents(waiting);
      clearDraft(instance.id);
      if (!closeTab) {
        setValues(savedValues);
      }
      const nextStepIndex = res.data.nextStep ?? step + 1;
      const updatedInstanceBase = {
        ...instance,
        currentStep: nextStepIndex,
        formData: savedValues,
        currentAssignee: res.data.currentAssignee || null,
        canClaim: res.data.canClaim ?? false,
        isCurrentAssignee: res.data.isCurrentAssignee ?? false,
        isPotentialResponsible: res.data.isPotentialResponsible ?? false,
      };
      if (res.data.done) {
        alert("Processo concluído");
        if (onFinish) onFinish();
        if (!closeTab) {
          setForm(null);
          setInstance({
            ...updatedInstanceBase,
            status: "COMPLETED",
          });
        }
        if (activateTab && returnToTabId) activateTab(returnToTabId);
        if (closeTab) closeTab({ activateTabId: returnToTabId });
        return;
      }

      if (waiting.length) {
        if (!closeTab) {
          setForm(null);
          setInstance(updatedInstanceBase);
        }
        return;
      } else {
        if (!closeTab) {
          setForm(res.data.form);
          setInstance(updatedInstanceBase);
        }
      }
      if (activateTab && returnToTabId) activateTab(returnToTabId);
      if (closeTab) closeTab({ activateTabId: returnToTabId });
    } catch (err) {
      const msg = err.response?.data?.error || "Erro ao enviar dados";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const canCancelInstance =
    !!instance &&
    Number(instance.currentStep ?? 0) === 0 &&
    !["completed", "cancelled"].includes(
      String(instance.status || "").toLowerCase(),
    );

  if (loading) return <p>Carregando...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (cancelled)
    return <p className="text-slate-600">Solicitação cancelada com sucesso.</p>;
  if (!form) {
    if (pendingEvents.length) {
      return (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-700">Eventos pendentes</h2>
          <p className="text-slate-600">
            Esta solicitação aguarda o disparo de um evento para prosseguir. Se você recebeu uma
            mensagem ou deseja acionar manualmente um temporizador, selecione a opção desejada.
          </p>
          <ul className="space-y-3">
            {pendingEvents.map((event) => (
              <li
                key={event.id}
                className="flex flex-col gap-2 rounded border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium text-slate-700">{event.name || event.id}</p>
                  {event.trigger && (
                    <p className="text-sm text-slate-500">Tipo: {event.trigger}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleTriggerEvent(event)}
                  disabled={triggeringEventId === event.id}
                  className="inline-flex items-center justify-center rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                >
                  {triggeringEventId === event.id ? "Registrando..." : "Registrar evento"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return <p>Nenhum formulário para exibir.</p>;
  }

  const groups = form.schema?.groups || [];
  const fields = form.schema?.fields || [];
  const modeMap = groups.reduce(
    (acc, g) => ({ ...acc, [g.id]: g.mode || "edit" }),
    {},
  );
  const totalSteps = instance?.process?.totalSteps || 0;

  const handleCancelInstance = async () => {
    if (!instance || !canCancelInstance || cancelling) return;
    const confirmed = window.confirm(
      "Deseja cancelar esta solicitação? Os dados preenchidos serão descartados.",
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      await api.delete(`/processes/instances/${instance.id}`);
      clearDraft(instance.id);
      alert("Solicitação cancelada com sucesso.");
      if (onFinish) onFinish();
      if (closeTab) {
        closeTab({ activateTabId: returnToTabId });
      } else {
        setCancelled(true);
        setForm(null);
        setInstance(null);
        setValues({});
        setPendingEvents([]);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Não foi possível cancelar a solicitação.";
      alert(msg);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {instance && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl">
              Solicitação {instance.id} - {instance.process.name}
            </h1>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowHistory((prev) => !prev)}
                className={`inline-flex items-center gap-2 rounded border px-3 py-1 text-sm font-medium transition ${
                  showHistory
                    ? "border-blue-600 bg-blue-50 text-blue-600"
                    : "border-slate-300 text-slate-600 hover:bg-slate-100"
                }`}
              >
                <FaHistory className="text-xs" />
                {showHistory ? "Ocultar histórico" : "Histórico"}
              </button>
              {canCancelInstance && (
                <button
                  type="button"
                  onClick={handleCancelInstance}
                  disabled={cancelling}
                  className="rounded bg-rose-600 px-3 py-1 text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-rose-400"
                >
                  {cancelling ? "Cancelando..." : "Cancelar solicitação"}
                </button>
              )}
              <button
                onClick={handleDownloadPdf}
                className="bg-blue-600 text-white px-3 py-1 rounded"
              >
                Baixar PDF
              </button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2 rounded shadow text-sm">
            <div>
              <span className="font-medium">ID:</span> {instance.id}
            </div>
            <div>
              <span className="font-medium">Processo:</span>{" "}
              {instance.process.name}
            </div>
            <div>
              <span className="font-medium">Solicitante:</span>{" "}
              {instance.requester?.name ||
                instance.user?.name ||
                instance.startedBy?.name ||
                "-"}
            </div>
            <div>
              <span className="font-medium">Início:</span>{" "}
              {new Date(instance.startedAt).toLocaleString()}
            </div>
          </div>
          {showHistory && (
            <div className="rounded border border-slate-200 bg-white p-3 shadow-sm">
              <StepHistory instanceId={instance.id} />
            </div>
          )}
          {(instance.currentAssignee?.id !== user?.id || instance.isCurrentAssignee) && (
            <div className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              {instance.currentAssignee && instance.currentAssignee.id !== user?.id && (
                <div className="text-sm text-slate-700">
                  <span className="font-semibold text-slate-800">Atribuído a:</span>{" "}
                  {instance.currentAssignee.name || `Usuário #${instance.currentAssignee.id}`}
                </div>
              )}
              {instance.isCurrentAssignee && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleReleaseTask}
                    disabled={reassigningTask}
                    className="rounded border border-slate-400 px-3 py-1 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {reassigningTask ? "Liberando..." : "Liberar"}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {!instance && <h1 className="text-2xl">{form.name}</h1>}
      {instance && (totalSteps > 0 || currentStepName) && (
        <p className="text-sm text-gray-600">
          {currentStepName ? `Etapa atual: ${currentStepName}` : "Etapa atual"}
        </p>
      )}
      {instance && mustClaimBeforeSubmit && (
        <p className="text-sm text-amber-600">
          {instance.currentAssignee && !instance.isCurrentAssignee
            ? `A tarefa está atribuída a ${
                instance.currentAssignee.name || `usuário #${instance.currentAssignee.id}`
              }. Você não pode enviar o formulário.`
            : "Assuma a tarefa antes de enviar o formulário."}
        </p>
      )}
      {groups.map((g) => {
        const gFields = fields.filter(
          (f) => f.groupId === g.id && fieldVisible(f, values),
        );
        if (gFields.length === 0) return null;
        return (
          <div key={g.id} className="mb-6 space-y-3">
            <h3 className="text-lg font-semibold text-slate-800">{g.label}</h3>
            <div className="grid grid-cols-12 gap-4">
              {gFields.map((f, idx) => {
                const groupMode = modeMap[f.groupId];
                const isGroupView = groupMode === "view";
                const dynamicReadOnly = Boolean(f.readOnly || isGroupView);
                const allowSelectionWhenReadOnly = Boolean(
                  f.lockedForStep && !isGroupView,
                );
                const columnSpan = f.size || 12;
                return (
                  <div
                    key={`${g.id}-${idx}`}
                    className="flex flex-col gap-2"
                    style={{ gridColumn: `span ${columnSpan} / span ${columnSpan}` }}
                  >
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor={f.id}
                      >
                        {f.label}
                        {f.required && <span className="text-red-600"> *</span>}
                      </label>
                      {f.description && (
                        <span className="text-xs leading-snug text-slate-500">
                          {f.description}
                        </span>
                      )}
                    </div>
                    {renderFieldControl(f, {
                      readOnly: dynamicReadOnly,
                      allowSelectionWhenReadOnly,
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {fields.filter((f) => !f.groupId && fieldVisible(f, values)).length >
        0 && (
        <div className="mb-6 space-y-3">
          {groups.length > 0 && (
            <h3 className="text-lg font-semibold text-slate-800">Campos</h3>
          )}
          <div className="grid grid-cols-12 gap-4">
            {fields
              .filter((f) => !f.groupId && fieldVisible(f, values))
              .map((f, idx) => {
                const columnSpan = f.size || 12;
                const readOnly = Boolean(f.readOnly);
                const allowSelectionWhenReadOnly = Boolean(f.lockedForStep);
                return (
                  <div
                    key={`nogroup-run-${idx}`}
                    className="flex flex-col gap-2"
                    style={{ gridColumn: `span ${columnSpan} / span ${columnSpan}` }}
                  >
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor={f.id}
                      >
                        {f.label}
                        {f.required && <span className="text-red-600"> *</span>}
                      </label>
                      {f.description && (
                        <span className="text-xs leading-snug text-slate-500">
                          {f.description}
                        </span>
                      )}
                    </div>
                    {renderFieldControl(f, {
                      readOnly,
                      allowSelectionWhenReadOnly,
                    })}
                  </div>
                );
              })}
          </div>
        </div>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {instance?.canClaim && !instance?.isCurrentAssignee && (
          <button
            type="button"
            onClick={handleClaimTask}
            disabled={claimingTask}
            className="inline-flex items-center justify-center rounded-md border border-blue-500 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {claimingTask ? "Assumindo..." : "Assumir tarefa"}
          </button>
        )}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSubmit}
          disabled={mustClaimBeforeSubmit || submitting}
        >
          {submitting ? "Enviando..." : "Enviar"}
        </button>
      </div>
    </div>
  );
}
